import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import TestimonialSection from './components/TestimonialSection';
import ContactSection from './components/ContactSection';
import StatsSection from './components/StatsSection';
import CatalogSection from './components/CatalogSection';
import QuoteRequestSection from './components/QuoteRequestSection';
import NewsletterSection from './components/NewsletterSection';
import AboutSection from './components/AboutSection';
import UserPopup from './components/UserPopup';
import AdminPanel from './admin/AdminPanel';

// Components
const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-cream-50/95 backdrop-blur-md shadow-2xl border-b border-cream-200 py-2' 
        : 'bg-transparent py-4'
    }`}>
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 sm:space-x-4">
            {/* Client Logo */}
            <div className="relative">
              <img 
                src="/logo.jpg" 
                alt="Kalaratan's Gifts Logo" 
                className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-4 border-primary-500 shadow-lg"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-luxury-400 rounded-full border-2 border-white"></div>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-display font-bold gradient-gold">
                Kalaratan's Gifts
              </h1>
              <p className="text-xs text-burlap-600 font-medium tracking-wider hidden sm:block">
                ARTISAN CRAFTED
              </p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {[
              { name: 'Home', href: '#home' },
              { name: 'About', href: '#about' },
              { name: 'Collections', href: '#catalog' },
              { name: 'Corporate Gifts', href: '#catalog' },
              { name: 'Festival Hampers', href: '#catalog' },
              { name: 'Contact', href: '#contact' }
            ].map((item) => (
              <a 
                key={item.name}
                href={item.href} 
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.querySelector(item.href);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="text-burlap-700 hover:text-primary-600 font-medium transition-colors duration-300 relative group text-sm uppercase tracking-wide py-2 cursor-pointer"
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-500 to-luxury-500 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden text-burlap-700 p-2 rounded-lg hover:bg-cream-100 transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-cream-200">
            <div className="flex flex-col space-y-3 pt-4">
              {[
                { name: 'Home', href: '#home' },
                { name: 'About', href: '#about' },
                { name: 'Collections', href: '#catalog' },
                { name: 'Corporate Gifts', href: '#catalog' },
                { name: 'Festival Hampers', href: '#catalog' },
                { name: 'Contact', href: '#contact' }
              ].map((item) => (
                <a 
                  key={item.name}
                  href={item.href} 
                  className="text-burlap-700 hover:text-primary-600 font-medium transition-colors duration-300 py-3 px-4 rounded-lg hover:bg-cream-100 text-center uppercase tracking-wide cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    const element = document.querySelector(item.href);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {item.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = () => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-cream-50 via-cream-100 to-burlap-100 burlap-texture px-4 sm:px-6">
      {/* Elegant Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-64 sm:w-96 h-64 sm:h-96 bg-primary-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute top-20 sm:top-40 right-10 sm:right-20 w-60 sm:w-80 h-60 sm:h-80 bg-luxury-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-10 sm:bottom-20 left-1/4 sm:left-1/3 w-48 sm:w-72 h-48 sm:h-72 bg-accent-300 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float" style={{animationDelay: '4s'}}></div>
      </div>
      
      <div className="relative container mx-auto text-center">
        <div className="animate-slide-down">
          {/* Brand Mark */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="relative">
              <img 
                src="/logo.jpg" 
                alt="Kalaratan's Gifts Logo" 
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-primary-500 shadow-2xl animate-glow"
              />
              <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-luxury-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold text-burlap-800 mb-3 sm:mb-4 leading-tight">
            <span className="gradient-gold">Kalaratan's</span>
            <br />
            <span className="text-accent-700">Gifts</span>
          </h1>
          
          <p className="text-sm sm:text-lg md:text-xl text-burlap-600 font-medium mb-3 sm:mb-4 tracking-wide uppercase px-4">
            Artisan Crafted • Premium Quality • Timeless Elegance
          </p>
          
          <p className="text-base sm:text-lg md:text-xl text-burlap-700 mb-8 sm:mb-12 max-w-4xl mx-auto font-light leading-relaxed px-4">
            Discover extraordinary gifts that embody craftsmanship and tradition. From corporate elegance to handcrafted treasures, 
            each piece tells a story of artistry and care, creating moments that last a lifetime.
          </p>
        </div>
        
        <div className="animate-slide-up flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mt-8 sm:mt-12 px-4">
          <button className="w-full sm:w-auto bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold py-4 px-8 sm:px-10 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 border-2 border-primary-400 group min-h-[48px]">
            <span className="flex items-center justify-center">
              Explore Collections
              <svg className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
              </svg>
            </span>
          </button>
          <button className="w-full sm:w-auto bg-transparent border-2 border-accent-400 text-accent-700 font-bold py-4 px-8 sm:px-10 rounded-full hover:bg-accent-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl min-h-[48px]">
            View Catalog
          </button>
        </div>
        
        {/* Floating Product Elements - Hidden on small screens */}
        <div className="hidden sm:block absolute top-32 left-32 animate-bounce-slow opacity-60">
          <div className="w-12 sm:w-16 h-16 sm:h-20 bg-gradient-to-br from-burlap-300 to-burlap-400 rounded-lg rotate-12 shadow-xl border border-burlap-200"></div>
        </div>
        <div className="hidden sm:block absolute bottom-48 right-32 animate-pulse-slow opacity-60">
          <div className="w-10 sm:w-12 h-10 sm:h-12 bg-gradient-to-br from-luxury-400 to-primary-400 rounded-full shadow-xl border-2 border-cream-200"></div>
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: "🏢",
      title: "Corporate Excellence",
      description: "Premium corporate gifting solutions that strengthen business relationships and leave lasting impressions.",
      gradient: "from-primary-400 to-primary-600",
      bgColor: "bg-cream-50",
      textColor: "text-burlap-800"
    },
    {
      icon: "🎭",
      title: "Traditional Heritage",
      description: "Authentic traditional crafts celebrating rich cultural heritage with timeless artistry and meaning.",
      gradient: "from-accent-400 to-accent-600", 
      bgColor: "bg-burlap-50",
      textColor: "text-accent-800"
    },
    {
      icon: "🎨",
      title: "Handmade Artistry",
      description: "Unique handcrafted treasures made with passion, showcasing exceptional skill and attention to detail.",
      gradient: "from-luxury-400 to-luxury-600",
      bgColor: "bg-primary-50",
      textColor: "text-luxury-800"
    },
    {
      icon: "✨",
      title: "Custom Creations",
      description: "Personalized gifts tailored to your vision, creating one-of-a-kind pieces for special moments.",
      gradient: "from-cream-400 to-cream-600",
      bgColor: "bg-accent-50", 
      textColor: "text-cream-800"
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-cream-50 to-burlap-50 burlap-texture px-4 sm:px-6">
      <div className="container mx-auto">
        <div className="text-center mb-16 sm:mb-20 animate-fade-in">
          <div className="inline-block mb-4">
            <span className="text-xs sm:text-sm font-bold text-primary-600 uppercase tracking-widest bg-primary-100 px-3 sm:px-4 py-2 rounded-full">
              Our Specialties
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-burlap-800 mb-4 sm:mb-6">
            Crafted with <span className="gradient-gold">Excellence</span>
          </h2>
          <p className="text-lg sm:text-xl text-burlap-600 max-w-4xl mx-auto leading-relaxed">
            We specialize in creating memorable experiences through carefully curated gifts that celebrate tradition, 
            artistry, and the moments that matter most in life and business.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`group card-hover ${feature.bgColor} rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl border-2 border-white/50 hover:border-primary-200 transition-all duration-500`}
              style={{animationDelay: `${index * 200}ms`}}
            >
              <div className={`w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-r ${feature.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center text-3xl sm:text-4xl mb-6 sm:mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg border-2 border-white/30 mx-auto sm:mx-0`}>
                {feature.icon}
              </div>
              <h3 className={`text-xl sm:text-2xl font-bold ${feature.textColor} mb-3 sm:mb-4 font-display text-center sm:text-left`}>{feature.title}</h3>
              <p className="text-burlap-600 leading-relaxed text-base sm:text-lg text-center sm:text-left">{feature.description}</p>
              
              {/* Decorative line */}
              <div className={`mt-4 sm:mt-6 h-1 w-12 sm:w-16 bg-gradient-to-r ${feature.gradient} rounded-full opacity-60 mx-auto sm:mx-0`}></div>
            </div>
          ))}
        </div>
        
        {/* Quality Badges */}
        <div className="mt-16 sm:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
          {[
            { icon: '🏆', title: 'Award Winning', desc: 'Recognized for Excellence' },
            { icon: '🌱', title: 'Eco Conscious', desc: 'Sustainable Materials' },
            { icon: '🤝', title: 'Trusted Partner', desc: '500+ Happy Clients' }
          ].map((badge, index) => (
            <div 
              key={index}
              className="text-center bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-cream-200 hover:bg-white/80 transition-all duration-300"
            >
              <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{badge.icon}</div>
              <h4 className="text-base sm:text-lg font-bold text-burlap-800 mb-2">{badge.title}</h4>
              <p className="text-burlap-600 text-sm">{badge.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-burlap-800 via-burlap-900 to-accent-900 text-white py-12 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 burlap-texture opacity-10"></div>
      
      <div className="relative container mx-auto px-6">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand Section */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Kalaratan's Gifts Logo" 
                className="w-12 h-12 rounded-full object-cover border-2 border-primary-500"
              />
              <div>
                <h3 className="text-xl font-display font-bold gradient-gold">Kalaratan's Gifts</h3>
                <p className="text-xs text-cream-300 uppercase tracking-wider">
                  Artisan Crafted Excellence
                </p>
              </div>
            </div>
            <p className="text-cream-200 text-sm leading-relaxed mb-4">
              Creating memorable moments through exceptional gifts, traditional craftsmanship, 
              and personalized service that celebrates life's most precious occasions.
            </p>
          </div>
          
          {/* Collections */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-cream-100 font-display">Collections</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {['Corporate Gifts', 'Traditional Crafts', 'Handmade Artistry', 'Luxury Items', 'Custom Orders', 'Gift Hampers'].map((link) => (
                <button key={link} className="text-cream-300 hover:text-primary-300 transition-colors duration-300 text-left">
                  {link}
                </button>
              ))}
            </div>
          </div>
          
          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-cream-100 font-display">Services</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {['Gift Consultation', 'Custom Design', 'Corporate Programs', 'Event Gifting', 'Bulk Orders', 'International Shipping'].map((service) => (
                <button key={service} className="text-cream-300 hover:text-primary-300 transition-colors duration-300 text-left">
                  {service}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Contact Info */}
        <div className="border-t border-cream-600/30 pt-6">
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="text-center md:text-left">
              <h5 className="font-semibold text-cream-100 mb-2">Visit Our Showroom</h5>
              <p className="text-cream-300 text-sm">Kalaratan's Gifts, MG Road</p>
              <p className="text-cream-300 text-sm">Bengaluru, Karnataka 560001</p>
            </div>
            <div className="text-center md:text-left">
              <h5 className="font-semibold text-cream-100 mb-2">Contact Information</h5>
              <p className="text-cream-300 text-sm">📧 kalaratans.gifts@gmail.com</p>
              <p className="text-cream-300 text-sm">📱 +91 8748845182</p>
              <p className="text-cream-300 text-sm">📱 @kalaratans.gifts</p>
            </div>
            <div className="text-center md:text-left">
              <h5 className="font-semibold text-cream-100 mb-2">Business Hours</h5>
              <p className="text-cream-300 text-sm">Mon-Fri: 9:00 AM - 6:00 PM</p>
              <p className="text-cream-300 text-sm">Sat-Sun: 10:00 AM - 4:00 PM</p>
            </div>
          </div>
        </div>
        
        {/* Newsletter */}
        <div className="border-t border-cream-600/30 pt-6 mb-6">
          <NewsletterSection />
        </div>
        
        {/* Copyright */}
        <div className="border-t border-cream-600/30 pt-4 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <p className="text-cream-400 text-sm">
              © 2025 Kalaratan's Gifts. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="/admins" 
                className="text-cream-500 hover:text-cream-300 text-xs transition-colors duration-300"
              >
                Admin
              </a>
              <p className="text-cream-500 text-xs">
                Designed & Developed by{' '}
                <a 
                  href="https://www.linkedin.com/in/kallesh-d-r-087008235/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-300 hover:text-primary-200 font-medium transition-colors duration-300"
                >
                  Kallesh
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admins" element={<AdminPanel />} />
        <Route path="/" element={
          <div className="App">
            <NavBar />
            <HeroSection />
            <AboutSection />
            <FeaturesSection />
            <CatalogSection />
            <QuoteRequestSection />
            <StatsSection />
            <TestimonialSection />
            <ContactSection />
            <Footer />
            <UserPopup />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
